import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { API, useApp } from "../state/AppContext";
import { cloudinaryThumb } from "../lib/cloudinary";
import { Play, HeartHandshake, ExternalLink, Heart } from "lucide-react";

const VISITOR_KEY = "scl_wall_visitor_id";
const getVisitorId = () => {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      const rand = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      v = `v-${rand}`;
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch { return `v-fallback-${Date.now()}`; }
};

export default function KindnessWallScreen() {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  const [wallPresets, setWallPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const visitorId = useRef(getVisitorId()).current;

  const fetchPresets = useCallback(() => {
    return axios.get(`${API}/wall/photos`, { params: { visitor_id: visitorId } })
      .then((res) => setWallPresets(res.data?.photos || []))
      .catch(() => {});
  }, [visitorId]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/wall`).then((res) => setItems(res.data)).catch(() => {}),
      fetchPresets(),
    ]).finally(() => setLoading(false));
  }, [fetchPresets]);

  // Lightweight polling — every 20s while the tab is visible — so classrooms
  // watching the wall see hearts trickle in without a manual refresh.
  useEffect(() => {
    let h = null;
    const tick = () => { if (document.visibilityState === "visible") fetchPresets(); };
    h = setInterval(tick, 20000);
    const onVis = () => { if (document.visibilityState === "visible") fetchPresets(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { if (h) clearInterval(h); document.removeEventListener("visibilitychange", onVis); };
  }, [fetchPresets]);

  const toggleReact = async (photo) => {
    // Optimistic update first — the heart should feel instant
    const next = !photo.reacted;
    setWallPresets((cur) => cur.map((p) => p.id === photo.id
      ? { ...p, reacted: next, reactions_count: Math.max(0, (p.reactions_count || 0) + (next ? 1 : -1)) }
      : p));
    try {
      const r = next
        ? await axios.post(`${API}/wall/photos/${photo.id}/react`, { visitor_id: visitorId })
        : await axios.delete(`${API}/wall/photos/${photo.id}/react`, { params: { visitor_id: visitorId } });
      setWallPresets((cur) => cur.map((p) => p.id === photo.id
        ? { ...p, reacted: r.data.reacted, reactions_count: r.data.count }
        : p));
    } catch {
      // Revert on failure
      setWallPresets((cur) => cur.map((p) => p.id === photo.id
        ? { ...p, reacted: photo.reacted, reactions_count: photo.reactions_count || 0 }
        : p));
    }
  };

  return (
    <div className="min-h-screen bg-scl-cream" data-testid="wall-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <img
            src="https://customer-assets-0z36b82j.emergentagent.net/job_scl-kindness/artifacts/6382qm3g_SCL-final-logo-clearspace-namelogo-cropped%20%282%29.png"
            alt="SCL"
            className="h-10 w-auto"
          />
          <div className="flex-1 min-w-[220px]">
            <div className="text-[11px] uppercase tracking-[0.18em] text-scl-teal font-semibold flex items-center gap-1.5">
              <HeartHandshake className="w-3.5 h-3.5" /> {t("wall_kicker")}
            </div>
            <div className="font-serif text-2xl sm:text-3xl text-scl-charcoal font-bold">{t("wall_title")}</div>
          </div>
          <a href="/" className="text-xs text-scl-charcoal/60 hover:text-scl-teal underline underline-offset-4 min-h-[44px] flex items-center">{t("wall_back")}</a>
        </div>

        {loading && <div className="text-scl-charcoal/60 text-sm">{t("loading")}</div>}

        {/* Staff-stamped Kindness Wall photos (Story preset). Only shows when
             teachers have posted at least one. */}
        {!loading && wallPresets.length > 0 && (
          <section className="mb-10" data-testid="wall-preset-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-scl-mustard flex items-center justify-center">
                <HeartHandshake className="w-4 h-4 text-scl-charcoal" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-scl-charcoal/70 font-semibold">From SCL classrooms</div>
                <div className="font-serif text-lg text-scl-charcoal font-bold">This week's Kindness Wall</div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {wallPresets.map((p) => (
                <div
                  key={p.id}
                  className="group relative rounded-2xl overflow-hidden bg-white shadow-warm-sm aspect-[9/16]"
                  data-testid={`wall-preset-${p.id}`}
                >
                  <a href={p.file_link} target="_blank" rel="noreferrer" aria-label="Open photo" className="absolute inset-0 block" data-testid={`wall-preset-open-${p.id}`}>
                    <img src={p.file_link} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  </a>
                  <div className="absolute top-2 left-2 bg-scl-coral text-white text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full pointer-events-none">SCL · Kindness</div>

                  {/* Reaction heart — public one-tap. Optimistic UI. */}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleReact(p); }}
                    data-testid={`wall-heart-${p.id}`}
                    data-reacted={p.reacted ? "true" : "false"}
                    aria-pressed={p.reacted ? "true" : "false"}
                    aria-label={p.reacted ? "Unlike" : "Send a heart"}
                    className={`absolute top-2 right-2 min-h-[36px] min-w-[36px] px-2 rounded-full flex items-center gap-1 shadow-warm-sm transition-all active:scale-90 ${p.reacted ? "bg-scl-brick text-white" : "bg-white/95 text-scl-brick hover:bg-white"}`}
                  >
                    <Heart className={`w-4 h-4 ${p.reacted ? "fill-current" : ""}`} strokeWidth={p.reacted ? 2 : 2.2} />
                    {p.reactions_count > 0 && (
                      <span data-testid={`wall-heart-count-${p.id}`} className="text-[11px] font-bold tabular-nums">{p.reactions_count}</span>
                    )}
                  </button>

                  {(p.notes || p.grade_class) && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-scl-charcoal/85 to-transparent p-3 pointer-events-none">
                      {p.grade_class && <div className="text-[10px] uppercase tracking-widest text-white/85 font-semibold">{p.grade_class}</div>}
                      {p.notes && <div className="text-[12px] text-white leading-tight line-clamp-2 mt-0.5 italic">&ldquo;{p.notes}&rdquo;</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {!loading && items.length === 0 && wallPresets.length === 0 && (
          <div className="bg-white rounded-2xl p-10 text-center text-scl-charcoal/60 shadow-warm-sm">
            {t("wall_empty")}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {items.map((s) => {
            const isVideo = s.media_type === "video" || s.media_type?.startsWith("video/");
            const isHttp = s.media_url?.startsWith("http");
            const thumb = isHttp ? cloudinaryThumb(s.media_url, { w: 600, h: 600 }) : (s.media_url ? `${API}/files/${s.media_url}` : null);
            return (
              <a
                key={s.id}
                href={s.media_url || "#"}
                target="_blank"
                rel="noreferrer"
                className="group relative rounded-2xl overflow-hidden bg-white shadow-warm-sm aspect-square"
                data-testid={`wall-item-${s.id}`}
              >
                {thumb && <img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-t from-scl-charcoal/85 via-scl-charcoal/20 to-transparent" />
                {isVideo && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-black/60 flex items-center justify-center">
                      <Play className="w-6 h-6 text-white ml-0.5" />
                    </div>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                  <div className="text-[10px] uppercase tracking-widest opacity-90 font-semibold">{t("wall_pts_by", { name: s.first_name, n: s.points })}</div>
                  <div className="font-serif text-[14px] font-bold leading-tight line-clamp-2 mt-1">{s.challenge_title}</div>
                  {s.caption && <div className="text-[11px] opacity-80 mt-1 line-clamp-2 italic">&ldquo;{s.caption}&rdquo;</div>}
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 text-scl-charcoal text-[10px] font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> {t("open")}
                </div>
              </a>
            );
          })}
        </div>

        <div className="mt-8 text-center text-xs text-scl-charcoal/50">
          {t("footer_project")} <span className="font-semibold text-scl-charcoal/70">School for Community Learning</span> · Indianapolis
        </div>
      </div>
    </div>
  );
}
